import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  Tabs,
  Tab,
  Chip,
  Divider,
  FormControlLabel,
  Switch,
  IconButton,
  Tooltip,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress
} from '@mui/material';
import {
  Search as SEOIcon,
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Language as LanguageIcon,
  Analytics as AnalyticsIcon,
  Share as ShareIcon,
  Code as CodeIcon,
  Image as ImageIcon,
  Link as LinkIcon,
  Public as PublicIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import { seoService } from '../../services/firebase';
import type { SEOSettings } from '../../types';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`seo-tabpanel-${index}`}
      aria-labelledby={`seo-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const ManageSEOPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [seoSettings, setSeoSettings] = useState<SEOSettings | null>(null);
  const [keywordDialogOpen, setKeywordDialogOpen] = useState(false);
  const [newKeyword, setNewKeyword] = useState('');
  const [hreflangDialogOpen, setHreflangDialogOpen] = useState(false);
  const [newHreflang, setNewHreflang] = useState({ lang: '', url: '' });

  useEffect(() => {
    fetchSEOSettings();
  }, []);

  const fetchSEOSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const settings = await seoService.getSEOSettings();
      
      if (settings) {
        setSeoSettings(settings);
      } else {
        // Varsayılan ayarları oluştur
        await seoService.createDefaultSEOSettings();
        const defaultSettings = await seoService.getSEOSettings();
        setSeoSettings(defaultSettings);
      }
    } catch (err) {
      console.error('SEO ayarları yüklenirken hata:', err);
      setError('SEO ayarları yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!seoSettings) return;
    
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      
      await seoService.updateSEOSettings(seoSettings);
      setSuccess('SEO ayarları başarıyla kaydedildi');
    } catch (err) {
      console.error('SEO ayarları kaydedilirken hata:', err);
      setError('SEO ayarları kaydedilirken bir hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const updateSetting = (path: string, value: any) => {
    if (!seoSettings) return;
    
    const keys = path.split('.');
    const newSettings = { ...seoSettings };
    let current: any = newSettings;
    
    for (let i = 0; i < keys.length - 1; i++) {
      current = current[keys[i]];
    }
    
    current[keys[keys.length - 1]] = value;
    setSeoSettings(newSettings);
  };

  const addKeyword = (section: string) => {
    if (!newKeyword.trim() || !seoSettings) return;
    
    if (section === 'default') {
      updateSetting('defaultMetaKeywords', [...seoSettings.defaultMetaKeywords, newKeyword.trim()]);
    } else if (section === 'site') {
      updateSetting('siteKeywords', [...seoSettings.siteKeywords, newKeyword.trim()]);
    } else {
      const currentKeywords = seoSettings.pageSettings[section as keyof typeof seoSettings.pageSettings]?.keywords || [];
      updateSetting(`pageSettings.${section}.keywords`, [...currentKeywords, newKeyword.trim()]);
    }
    
    setNewKeyword('');
    setKeywordDialogOpen(false);
  };

  const removeKeyword = (section: string, index: number) => {
    if (!seoSettings) return;
    
    if (section === 'default') {
      const keywords = [...seoSettings.defaultMetaKeywords];
      keywords.splice(index, 1);
      updateSetting('defaultMetaKeywords', keywords);
    } else if (section === 'site') {
      const keywords = [...seoSettings.siteKeywords];
      keywords.splice(index, 1);
      updateSetting('siteKeywords', keywords);
    } else {
      const currentKeywords = [...(seoSettings.pageSettings[section as keyof typeof seoSettings.pageSettings]?.keywords || [])];
      currentKeywords.splice(index, 1);
      updateSetting(`pageSettings.${section}.keywords`, currentKeywords);
    }
  };

  const addHreflang = () => {
    if (!newHreflang.lang.trim() || !newHreflang.url.trim() || !seoSettings) return;
    
    updateSetting('hreflangTags', [...seoSettings.hreflangTags, { ...newHreflang }]);
    setNewHreflang({ lang: '', url: '' });
    setHreflangDialogOpen(false);
  };

  const removeHreflang = (index: number) => {
    if (!seoSettings) return;
    
    const tags = [...seoSettings.hreflangTags];
    tags.splice(index, 1);
    updateSetting('hreflangTags', tags);
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!seoSettings) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error">SEO ayarları yüklenemedi</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <SEOIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            SEO Yönetimi
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Site SEO ayarlarını yönetin ve arama motoru optimizasyonunu geliştirin.
        </Typography>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Kaydediliyor...' : 'Kaydet'}
        </Button>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchSEOSettings}
        >
          Yenile
        </Button>
      </Box>

      {/* Main Content */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
            <Tab icon={<PublicIcon />} label="Genel Ayarlar" />
            <Tab icon={<ShareIcon />} label="Meta Etiketleri" />
            <Tab icon={<ImageIcon />} label="Open Graph" />
            <Tab icon={<LinkIcon />} label="Twitter Cards" />
            <Tab icon={<SecurityIcon />} label="Schema.org" />
            <Tab icon={<CodeIcon />} label="Teknik SEO" />
            <Tab icon={<AnalyticsIcon />} label="Analytics" />
            <Tab icon={<LanguageIcon />} label="Sayfa Ayarları" />
          </Tabs>
        </Box>

        {/* Genel Ayarlar */}
        <TabPanel value={tabValue} index={0}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Genel Site Ayarları</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              <TextField
                fullWidth
                label="Site Adı"
                value={seoSettings.siteName}
                onChange={(e) => updateSetting('siteName', e.target.value)}
                margin="normal"
              />
              <TextField
                fullWidth
                label="Site URL"
                value={seoSettings.siteUrl}
                onChange={(e) => updateSetting('siteUrl', e.target.value)}
                margin="normal"
              />
              <Box sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}>
                <TextField
                  fullWidth
                  label="Site Açıklaması"
                  value={seoSettings.siteDescription}
                  onChange={(e) => updateSetting('siteDescription', e.target.value)}
                  multiline
                  rows={3}
                  margin="normal"
                />
              </Box>
              <TextField
                fullWidth
                label="Varsayılan Dil"
                value={seoSettings.defaultLanguage}
                onChange={(e) => updateSetting('defaultLanguage', e.target.value)}
                margin="normal"
              />
            </Box>

            <Box sx={{ mt: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Typography variant="subtitle1">Site Anahtar Kelimeleri</Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setKeywordDialogOpen(true)}
                >
                  Ekle
                </Button>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {seoSettings.siteKeywords.map((keyword, index) => (
                  <Chip
                    key={index}
                    label={keyword}
                    onDelete={() => removeKeyword('site', index)}
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          </CardContent>
        </TabPanel>

        {/* Meta Etiketleri */}
        <TabPanel value={tabValue} index={1}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Meta Etiketleri</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                fullWidth
                label="Varsayılan Başlık"
                value={seoSettings.defaultTitle}
                onChange={(e) => updateSetting('defaultTitle', e.target.value)}
                margin="normal"
              />
              <TextField
                fullWidth
                label="Başlık Şablonu"
                value={seoSettings.titleTemplate}
                onChange={(e) => updateSetting('titleTemplate', e.target.value)}
                margin="normal"
                helperText="Örnek: %s | HatTravel (%s sayfa başlığını temsil eder)"
              />
              <TextField
                fullWidth
                label="Varsayılan Meta Açıklama"
                value={seoSettings.defaultMetaDescription}
                onChange={(e) => updateSetting('defaultMetaDescription', e.target.value)}
                multiline
                rows={3}
                margin="normal"
              />
            </Box>

            <Box sx={{ mt: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Typography variant="subtitle1">Varsayılan Meta Anahtar Kelimeler</Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setKeywordDialogOpen(true)}
                >
                  Ekle
                </Button>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {seoSettings.defaultMetaKeywords.map((keyword: string, index: number) => (
                  <Chip
                    key={index}
                    label={keyword}
                    onDelete={() => removeKeyword('default', index)}
                    color="secondary"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          </CardContent>
        </TabPanel>

        {/* Open Graph */}
        <TabPanel value={tabValue} index={2}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Open Graph Ayarları</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Facebook ve diğer sosyal medya platformlarında paylaşıldığında görünecek bilgileri ayarlayın.
            </Typography>
            
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              <TextField
                fullWidth
                label="OG Site Adı"
                value={seoSettings.openGraph?.siteName || ''}
                onChange={(e) => updateSetting('openGraph.siteName', e.target.value)}
                margin="normal"
              />
              <TextField
                fullWidth
                label="OG Tip"
                value={seoSettings.openGraph?.type || 'website'}
                onChange={(e) => updateSetting('openGraph.type', e.target.value)}
                margin="normal"
                helperText="Örnek: website, article, product"
              />
              <Box sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}>
                <TextField
                  fullWidth
                  label="OG Varsayılan Resim URL"
                  value={seoSettings.openGraph?.defaultImage || ''}
                  onChange={(e) => updateSetting('openGraph.defaultImage', e.target.value)}
                  margin="normal"
                  helperText="Sosyal medyada paylaşıldığında görünecek varsayılan resim"
                />
              </Box>
              <TextField
                fullWidth
                label="OG Locale"
                value={seoSettings.openGraph?.locale || 'tr_TR'}
                onChange={(e) => updateSetting('openGraph.locale', e.target.value)}
                margin="normal"
                helperText="Örnek: tr_TR, en_US, ar_AR"
              />
            </Box>
          </CardContent>
        </TabPanel>

        {/* Twitter Cards */}
        <TabPanel value={tabValue} index={3}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Twitter Cards Ayarları</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Twitter'da paylaşıldığında görünecek kart ayarlarını yapılandırın.
            </Typography>
            
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              <TextField
                fullWidth
                label="Twitter Card Tipi"
                value={seoSettings.twitterCard?.cardType || 'summary_large_image'}
                onChange={(e) => updateSetting('twitterCard.cardType', e.target.value)}
                margin="normal"
                helperText="summary, summary_large_image, app, player"
              />
              <TextField
                fullWidth
                label="Twitter Site"
                value={seoSettings.twitterCard?.site || ''}
                onChange={(e) => updateSetting('twitterCard.site', e.target.value)}
                margin="normal"
                helperText="@kullaniciadi formatında"
              />
              <TextField
                fullWidth
                label="Twitter Creator"
                value={seoSettings.twitterCard?.creator || ''}
                onChange={(e) => updateSetting('twitterCard.creator', e.target.value)}
                margin="normal"
                helperText="@kullaniciadi formatında"
              />
              <TextField
                fullWidth
                label="Twitter Varsayılan Resim"
                value={seoSettings.twitterCard?.defaultImage || ''}
                onChange={(e) => updateSetting('twitterCard.defaultImage', e.target.value)}
                margin="normal"
                helperText="Twitter'da görünecek varsayılan resim URL'si"
              />
            </Box>
          </CardContent>
        </TabPanel>

        {/* Schema.org */}
        <TabPanel value={tabValue} index={4}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Schema.org Yapılandırılmış Veri</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Arama motorlarının sitenizi daha iyi anlaması için yapılandırılmış veri ayarları.
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={seoSettings.schema?.enableOrganizationSchema || false}
                    onChange={(e) => updateSetting('schema.enableOrganizationSchema', e.target.checked)}
                  />
                }
                label="Organizasyon Şeması Etkinleştir"
              />
              
              <TextField
                fullWidth
                label="Organizasyon Adı"
                value={seoSettings.schema?.organizationName || ''}
                onChange={(e) => updateSetting('schema.organizationName', e.target.value)}
                margin="normal"
                disabled={!seoSettings.schema?.enableOrganizationSchema}
              />
              
              <TextField
                fullWidth
                label="Organizasyon Logo URL"
                value={seoSettings.schema?.organizationLogo || ''}
                onChange={(e) => updateSetting('schema.organizationLogo', e.target.value)}
                margin="normal"
                disabled={!seoSettings.schema?.enableOrganizationSchema}
              />
              
              <TextField
                fullWidth
                label="Organizasyon Telefon"
                value={seoSettings.schema?.organizationPhone || ''}
                onChange={(e) => updateSetting('schema.organizationPhone', e.target.value)}
                margin="normal"
                disabled={!seoSettings.schema?.enableOrganizationSchema}
              />
              
              <TextField
                fullWidth
                label="Organizasyon E-posta"
                value={seoSettings.schema?.organizationEmail || ''}
                onChange={(e) => updateSetting('schema.organizationEmail', e.target.value)}
                margin="normal"
                disabled={!seoSettings.schema?.enableOrganizationSchema}
              />
              
              <TextField
                fullWidth
                label="Organizasyon Adres"
                value={seoSettings.schema?.organizationAddress || ''}
                onChange={(e) => updateSetting('schema.organizationAddress', e.target.value)}
                multiline
                rows={3}
                margin="normal"
                disabled={!seoSettings.schema?.enableOrganizationSchema}
              />
            </Box>
          </CardContent>
        </TabPanel>

        {/* Teknik SEO */}
        <TabPanel value={tabValue} index={5}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Teknik SEO Ayarları</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Arama motoru indeksleme ve teknik SEO ayarları.
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={seoSettings.technical?.enableRobotsTxt || false}
                    onChange={(e) => updateSetting('technical.enableRobotsTxt', e.target.checked)}
                  />
                }
                label="Robots.txt Etkinleştir"
              />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={seoSettings.technical?.enableSitemap || false}
                    onChange={(e) => updateSetting('technical.enableSitemap', e.target.checked)}
                  />
                }
                label="XML Sitemap Etkinleştir"
              />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={seoSettings.technical?.enableCanonical || false}
                    onChange={(e) => updateSetting('technical.enableCanonical', e.target.checked)}
                  />
                }
                label="Canonical URL'ler Etkinleştir"
              />
              
              <TextField
                fullWidth
                label="Robots.txt İçeriği"
                value={seoSettings.technical?.robotsTxtContent || ''}
                onChange={(e) => updateSetting('technical.robotsTxtContent', e.target.value)}
                multiline
                rows={6}
                margin="normal"
                disabled={!seoSettings.technical?.enableRobotsTxt}
                helperText="User-agent: *\nAllow: /\nDisallow: /admin/\nSitemap: https://yoursite.com/sitemap.xml"
              />
            </Box>
          </CardContent>
        </TabPanel>

        {/* Analytics */}
        <TabPanel value={tabValue} index={6}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Analytics ve İzleme</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Google Analytics ve diğer izleme araçlarının ayarları.
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                fullWidth
                label="Google Analytics ID"
                value={seoSettings.analytics?.googleAnalyticsId || ''}
                onChange={(e) => updateSetting('analytics.googleAnalyticsId', e.target.value)}
                margin="normal"
                helperText="G-XXXXXXXXXX formatında"
              />
              
              <TextField
                fullWidth
                label="Google Tag Manager ID"
                value={seoSettings.analytics?.googleTagManagerId || ''}
                onChange={(e) => updateSetting('analytics.googleTagManagerId', e.target.value)}
                margin="normal"
                helperText="GTM-XXXXXXX formatında"
              />
              
              <TextField
                fullWidth
                label="Facebook Pixel ID"
                value={seoSettings.analytics?.facebookPixelId || ''}
                onChange={(e) => updateSetting('analytics.facebookPixelId', e.target.value)}
                margin="normal"
                helperText="Facebook reklam izleme için"
              />
              
              <TextField
                fullWidth
                label="Özel Analytics Kodu"
                value={seoSettings.analytics?.customAnalyticsCode || ''}
                onChange={(e) => updateSetting('analytics.customAnalyticsCode', e.target.value)}
                multiline
                rows={6}
                margin="normal"
                helperText="Head bölümüne eklenecek özel JavaScript kodu"
              />
            </Box>
          </CardContent>
        </TabPanel>

        {/* Sayfa Ayarları */}
        <TabPanel value={tabValue} index={7}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Sayfa Özel SEO Ayarları</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Belirli sayfalar için özel SEO ayarlarını yapılandırın.
            </Typography>
            
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">Ana Sayfa SEO</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    fullWidth
                    label="Ana Sayfa Başlığı"
                    value={seoSettings.pageSettings?.home?.title || ''}
                    onChange={(e) => updateSetting('pageSettings.home.title', e.target.value)}
                    margin="normal"
                  />
                  <TextField
                    fullWidth
                    label="Ana Sayfa Meta Açıklama"
                    value={seoSettings.pageSettings?.home?.description || ''}
                    onChange={(e) => updateSetting('pageSettings.home.description', e.target.value)}
                    multiline
                    rows={3}
                    margin="normal"
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Typography variant="subtitle2">Ana Sayfa Anahtar Kelimeleri</Typography>
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => setKeywordDialogOpen(true)}
                    >
                      Ekle
                    </Button>
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {(seoSettings.pageSettings?.home?.keywords || []).map((keyword: string, index: number) => (
                      <Chip
                        key={index}
                        label={keyword}
                        onDelete={() => removeKeyword('home', index)}
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>
            
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">Turlar Sayfası SEO</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    fullWidth
                    label="Turlar Sayfası Başlığı"
                    value={seoSettings.pageSettings?.tours?.title || ''}
                    onChange={(e) => updateSetting('pageSettings.tours.title', e.target.value)}
                    margin="normal"
                  />
                  <TextField
                    fullWidth
                    label="Turlar Sayfası Meta Açıklama"
                    value={seoSettings.pageSettings?.tours?.description || ''}
                    onChange={(e) => updateSetting('pageSettings.tours.description', e.target.value)}
                    multiline
                    rows={3}
                    margin="normal"
                  />
                </Box>
              </AccordionDetails>
            </Accordion>
            
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">İletişim Sayfası SEO</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    fullWidth
                    label="İletişim Sayfası Başlığı"
                    value={seoSettings.pageSettings?.contact?.title || ''}
                    onChange={(e) => updateSetting('pageSettings.contact.title', e.target.value)}
                    margin="normal"
                  />
                  <TextField
                    fullWidth
                    label="İletişim Sayfası Meta Açıklama"
                    value={seoSettings.pageSettings?.contact?.description || ''}
                    onChange={(e) => updateSetting('pageSettings.contact.description', e.target.value)}
                    multiline
                    rows={3}
                    margin="normal"
                  />
                </Box>
              </AccordionDetails>
            </Accordion>
          </CardContent>
        </TabPanel>

        {/* Keyword Dialog */}
        <Dialog open={keywordDialogOpen} onClose={() => setKeywordDialogOpen(false)}>
          <DialogTitle>Anahtar Kelime Ekle</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              fullWidth
              label="Anahtar Kelime"
              value={newKeyword}
              onChange={(e) => setNewKeyword(e.target.value)}
              margin="normal"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setKeywordDialogOpen(false)}>İptal</Button>
            <Button onClick={() => addKeyword('default')} variant="contained">Ekle</Button>
          </DialogActions>
        </Dialog>

        {/* Hreflang Dialog */}
        <Dialog open={hreflangDialogOpen} onClose={() => setHreflangDialogOpen(false)}>
          <DialogTitle>Hreflang Etiketi Ekle</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              fullWidth
              label="Dil Kodu (örn: en, tr, de)"
              value={newHreflang.lang}
              onChange={(e) => setNewHreflang({ ...newHreflang, lang: e.target.value })}
              margin="normal"
            />
            <TextField
              fullWidth
              label="URL"
              value={newHreflang.url}
              onChange={(e) => setNewHreflang({ ...newHreflang, url: e.target.value })}
              margin="normal"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setHreflangDialogOpen(false)}>İptal</Button>
            <Button onClick={addHreflang} variant="contained">Ekle</Button>
          </DialogActions>
        </Dialog>
      </Card>
    </Container>
  );
};

export default ManageSEOPage; 